import { UploadOutlined } from "@ant-design/icons"
import { App, Button, Checkbox, DatePicker, Drawer, Flex, Form, Grid, Tag, Typography, Upload, notification } from "antd"
import type { UploadFile } from "antd"
import type { RcFile } from "antd/es/upload/interface"
import type { BaseOptionType } from "antd/lib/select"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"

import { HintedLabel } from "../../../../../../../components/FieldHint.tsx"
import { MODAL_CONFIRM_CONFIG } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import InputNumber from "../../../../../../../components/Input/InputNumber.tsx"
import { notificationOptions } from "../../../../../../../constants/notification.config.ts"
import { useAppDispatch } from "../../../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../../../hooks/use-app-selector.ts"
import { useDateFormat } from "../../../../../../../hooks/use-date-format.ts"
import { validateFile } from "../../../../../../../utils/file-validation.ts"
import { dateAutoFill } from "../../../../../../../utils/form/date-auto-fill.tsx"
import { validateFormWithScroll } from "../../../../../../../utils/form/validate-form-with-scroll.tsx"
import { isFormChanged } from "../../../../../../../utils/object/is-form-changed.ts"
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_ALLOWED_EXTENSIONS,
  ATTACHMENT_MAX_SIZE_MB
} from "../../../../../../Album/attachment.constants.ts"
import { AttachmentModels } from "../../../../../../Album/types/attachment.types.ts"
import { uploadProjectAttachment } from "../../../../../redux/project-attachments/project-attachments.thunks.ts"
import {
  createInvoice,
  fetchInvoicesFilterOptions
} from "../../../../../redux/project-invoices/project-invoices.thunks.ts"
import type { ProjectInvoice } from "../../../../../types/project-invoices.types.tsx"
import { invoiceFormFieldErrors } from "../../../../../../Invoices/helpers/handle-invoice-api-error.ts"
import { fetchBillableLines } from "../../../../../../Invoices/redux/invoices.thunks.ts"
import {
  INVOICE_STATUSES,
  type InvoiceLineInput
} from "../../../../../../Invoices/types/invoices.types.ts"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import {
  INVOICE_STATUSES_MAP
} from "../../../../constants/invoice-statuses.ts"
import { ProjectViewInvoiceAssigneeSelect } from "../../ProjectViewInvoices/ProjectViewInvoices/ProjectViewInvoiceAssigneeSelect.tsx"
import {
  buildInvoiceCreatePayload,
  defaultLinkedLineAmount,
  isInvoiceAmountAbovePayment,
  linkedLinesExceedInvoice,
  linkedLinesTotal,
  partitionUploadResults
} from "./quick-add-invoice.utils.ts"
import { ResolvedSelect } from "../../../../../../../components/Select/ResolvedSelect.tsx"
import { TextInput } from "../../../../../../../components/Input/TextInput.tsx"

interface QuickAddInvoiceDrawerProps {
  open: boolean
  onClose: () => void
  onCreated: (invoice: ProjectInvoice) => void
  selfAssigned?: boolean
  userId?: string
  paymentAmount?: number | null
  preselectedLineId?: string
}

const EMPTY_INVOICE = {} as ProjectInvoice

export const QuickAddInvoiceDrawer = ({
  open,
  onClose,
  onCreated,
  selfAssigned,
  userId,
  paymentAmount,
  preselectedLineId
}: QuickAddInvoiceDrawerProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const projectId = useParams()?.id as string
  const { getDateValueProps, createDateRangeRules, normalizeDate } =
    useDateFormat()
  const { modal } = App.useApp()
  const screens = Grid.useBreakpoint()

  const [form] = Form.useForm<ProjectInvoice>()
  const amountValue = Form.useWatch("amount", form)
  const invoiceExceedsPayment = isInvoiceAmountAbovePayment(
    amountValue,
    paymentAmount
  )
  const [saving, setSaving] = useState(false)
  const [options, setOptions] = useState<Record<string, BaseOptionType[]>>({})
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [createdInvoice, setCreatedInvoice] = useState<ProjectInvoice | null>(
    null
  )
  const baselineRef = useRef<ProjectInvoice>(EMPTY_INVOICE)

  const billableLines = useAppSelector((state) => state.invoices.billableLines)
  const [linkedLines, setLinkedLines] = useState<Record<string, number>>({})
  const linkedTotal = linkedLinesTotal(linkedLines)
  const linkedLinesExceed = linkedLinesExceedInvoice(
    linkedLines,
    amountValue != null ? Number(amountValue) : null
  )

  useEffect(() => {
    if (!open || !projectId) return
    void dispatch(fetchBillableLines(projectId))
  }, [open, projectId, dispatch])

  useEffect(() => {
    if (!open || !preselectedLineId) return
    const line = billableLines.find((item) => item.id === preselectedLineId)
    if (!line) return
    setLinkedLines((prev) => {
      if (preselectedLineId in prev) return prev
      return {
        ...prev,
        [preselectedLineId]: defaultLinkedLineAmount(
          line.balance_remaining,
          paymentAmount != null && paymentAmount > 0 ? paymentAmount : null,
          0
        )
      }
    })
  }, [open, preselectedLineId, billableLines, paymentAmount])

  useEffect(() => {
    if (!open || !projectId) return

    form.resetFields()
    setFileList([])
    setCreatedInvoice(null)
    setLinkedLines({})
    if (selfAssigned && userId) form.setFieldValue("assignee", userId)
    if (paymentAmount != null && paymentAmount > 0) {
      form.setFieldValue("amount", paymentAmount)
    }
    baselineRef.current = form.getFieldsValue()
    void dispatch(fetchInvoicesFilterOptions({ project_id: projectId }))
      .unwrap()
      .then(setOptions)
      .catch(() => undefined)
  }, [open, projectId, dispatch, form, selfAssigned, userId, paymentAmount])

  const stageFile = (file: RcFile) => {
    const valid = validateFile(file, {
      maxSize: ATTACHMENT_MAX_SIZE_MB,
      allowedExtensions: ATTACHMENT_ALLOWED_EXTENSIONS,
      defaultResult: true
    })

    if (valid === true) {
      setFileList((prev) => [
        ...prev,
        { uid: file.uid, name: file.name, originFileObj: file }
      ])
    }

    return false
  }

  const removeFile = (target: UploadFile) => {
    if (target.status === "done") return false
    setFileList((prev) => prev.filter((f) => f.uid !== target.uid))
    return false
  }

  const uploadStagedFiles = async (invoice: ProjectInvoice) => {
    const pending = fileList.filter(
      (f) => f.status !== "done" && f.originFileObj
    )

    if (pending.length === 0) return { failedNames: [] as string[] }

    const pendingUids = new Set(pending.map((f) => f.uid))
    setFileList((prev) =>
      prev.map((f) =>
        pendingUids.has(f.uid) ? { ...f, status: "uploading" } : f
      )
    )

    const results = await Promise.allSettled(
      pending.map((f) =>
        dispatch(
          uploadProjectAttachment({
            data: {
              model_type: AttachmentModels.INVOICE,
              object_id: invoice.id,
              project_id: projectId,
              file: f.originFileObj as RcFile
            },
            silent: true
          })
        ).unwrap()
      )
    )

    const { doneUids, failedNames } = partitionUploadResults(pending, results)
    const doneSet = new Set(doneUids)
    setFileList((prev) =>
      prev.map((f) =>
        pendingUids.has(f.uid)
          ? { ...f, status: doneSet.has(f.uid) ? "done" : "error" }
          : f
      )
    )

    return { failedNames }
  }

  const handleSave = async () => {
    let invoice = createdInvoice

    if (!invoice) {
      let values: ProjectInvoice
      try {
        values = await validateFormWithScroll(form)
      } catch {
        return
      }

      if (linkedLinesExceed) return

      try {
        setSaving(true)
        const payload = buildInvoiceCreatePayload(
          values,
          projectId,
          normalizeDate
        )
        const lines: InvoiceLineInput[] = Object.entries(linkedLines).map(
          ([financeLine, amount]) => ({
            finance_line: financeLine,
            amount: String(amount)
          })
        )
        invoice = await dispatch(
          createInvoice({
            ...payload,
            status: INVOICE_STATUSES.ISSUED,
            ...(lines.length ? { lines } : {}),
            silent: fileList.length > 0
          })
        ).unwrap()
        setCreatedInvoice(invoice)
      } catch (error) {
        setSaving(false)
        const fieldErrors = invoiceFormFieldErrors(error)
        if (fieldErrors.length) {
          form.setFields(
            fieldErrors.map(({ name, errors }) => ({
              name: name as keyof ProjectInvoice,
              errors
            }))
          )
          form.scrollToField(fieldErrors[0].name, { block: "center" })
        }
        return
      }
    } else {
      setSaving(true)
    }

    const attachedCount = fileList.length
    const { failedNames } = await uploadStagedFiles(invoice)
    setSaving(false)

    if (failedNames.length > 0) {
      notification.warning({
        ...notificationOptions.warning,
        message: t("finance.quickAddInvoice.attachments.partialError", {
          count: failedNames.length
        }),
        description: t("finance.quickAddInvoice.attachments.partialErrorHint")
      })
      return
    }

    if (attachedCount > 0) {
      notification.success({
        ...notificationOptions.success,
        message: t(
          "finance.quickAddInvoice.attachments.createdWithAttachments",
          {
            count: attachedCount
          }
        )
      })
    }

    onCreated(invoice)
  }

  const handleClose = () => {
    if (saving) return

    if (createdInvoice) {
      onCreated(createdInvoice)
      return
    }

    const changed =
      fileList.length > 0 ||
      Object.keys(linkedLines).length > 0 ||
      isFormChanged<ProjectInvoice>(form.getFieldsValue(), baselineRef.current)

    if (changed) {
      modal.confirm({ ...MODAL_CONFIRM_CONFIG.cancelChanges, onOk: onClose })
    } else {
      onClose()
    }
  }

  return (
    <Drawer
      title={t("finance.quickAddInvoice.title")}
      placement="right"
      width={screens.md ? 520 : "100%"}
      open={open}
      onClose={handleClose}
      closable={!saving}
      maskClosable={!saving}
      keyboard={!saving}
      footer={
        <Flex justify="flex-end" gap="small">
          <Button onClick={handleClose} disabled={saving}>
            {createdInvoice
              ? t("finance.quickAddInvoice.attachments.close")
              : t("finance.quickAddInvoice.cancel")}
          </Button>
          <Button
            type="primary"
            loading={saving}
            disabled={!createdInvoice && linkedLinesExceed}
            onClick={handleSave}
            data-testid="quick-add-invoice-submit"
          >
            {createdInvoice
              ? t("finance.quickAddInvoice.attachments.retry")
              : t("finance.quickAddInvoice.create")}
          </Button>
        </Flex>
      }
    >
      <Form<ProjectInvoice>
        form={form}
        layout="vertical"
        // Required: Form.less hides .ant-form-item-explain without this class, so
        // validation messages would render as a coloured border only, no text.
        className="with-default-error"
        disabled={saving || !!createdInvoice}
      >
        <Typography.Title level={5}>
          {t("finance.quickAddInvoice.sections.details")}
        </Typography.Title>

        <Form.Item<ProjectInvoice>
          name="invoice_number"
          label={t("finance.quickAddInvoice.fields.invoiceNumber")}
          rules={[
            {
              required: true,
              message: t("finance.quickAddInvoice.validation.required")
            },
            {
              max: 50,
              message: t("finance.quickAddInvoice.validation.invoiceNumberMax")
            }
          ]}
        >
          <TextInput
            disableAutoCapitalize
            data-testid="quick-add-invoice-number"
            placeholder={t(
              "finance.quickAddInvoice.placeholders.invoiceNumber"
            )}
            autoFocus
          />
        </Form.Item>

        <Flex gap="middle">
          <Form.Item<ProjectInvoice>
            className="flex-1"
            name="date_issued"
            label={t("finance.quickAddInvoice.fields.dateIssued")}
            getValueProps={getDateValueProps}
            dependencies={["due_date"]}
            rules={[
              {
                required: true,
                message: t("finance.quickAddInvoice.validation.required")
              },
              ...createDateRangeRules<ProjectInvoice>("due_date", "start")
            ]}
          >
            <DatePicker
              className="w-full"
              onChange={dateAutoFill<ProjectInvoice>({
                form,
                endDatePath: ["due_date"]
              })}
            />
          </Form.Item>

          <Form.Item<ProjectInvoice>
            className="flex-1"
            name="due_date"
            label={t("finance.quickAddInvoice.fields.dueDate")}
            getValueProps={getDateValueProps}
            dependencies={["date_issued"]}
            rules={[
              {
                required: true,
                message: t("finance.quickAddInvoice.validation.required")
              },
              ...createDateRangeRules<ProjectInvoice>("date_issued", "end")
            ]}
          >
            <DatePicker className="w-full" />
          </Form.Item>
        </Flex>

        <Form.Item<ProjectInvoice>
          name="amount"
          label={t("finance.quickAddInvoice.fields.amount")}
          rules={[
            {
              required: true,
              message: t("finance.quickAddInvoice.validation.required")
            }
          ]}
          // Advisory only: an invoice above the payment it covers is allowed, so
          // warn without blocking the save.
          validateStatus={invoiceExceedsPayment ? "warning" : undefined}
          help={
            invoiceExceedsPayment
              ? t("finance.quickAddInvoice.validation.amountExceedsPayment", {
                  amount: `$${paymentAmount}`
                })
              : undefined
          }
        >
          <InputNumber
            min={0}
            className="w-full"
            prefix="$"
            placeholder="0.00"
          />
        </Form.Item>

        <Typography.Title level={5}>
          {t("finance.quickAddInvoice.sections.classification")}
        </Typography.Title>

        <Form.Item<ProjectInvoice>
          name="type"
          label={t("finance.quickAddInvoice.fields.type")}
          rules={[
            {
              required: true,
              message: t("finance.quickAddInvoice.validation.required")
            }
          ]}
        >
          <ResolvedSelect
            placeholder={t("finance.quickAddInvoice.placeholders.select")}
            options={options?.["type"]}
          />
        </Form.Item>

        <Form.Item label={t("finance.quickAddInvoice.fields.status")}>
          <Tag
            style={INVOICE_STATUSES_MAP[INVOICE_STATUSES.ISSUED].style}
            className="m-0"
          >
            {t("finance.quickAddInvoice.issuedStatus")}
          </Tag>
        </Form.Item>

        <Form.Item label={t("finance.quickAddInvoice.fields.assignee")}>
          <ProjectViewInvoiceAssigneeSelect
            form={form}
            disabled={selfAssigned || undefined}
            options={{
              subcontractors: options?.["subcontractors"] as
                | { label: string; value: string | number }[]
                | undefined,
              customers: options?.["customers"] as
                | { label: string; value: string | number }[]
                | undefined
            }}
          />
        </Form.Item>

        <Form.Item<ProjectInvoice>
          name="service_type"
          label={t("finance.quickAddInvoice.fields.serviceType")}
          normalize={(v) => v || null}
        >
          <ResolvedSelect
            allowClear
            placeholder={t("finance.quickAddInvoice.placeholders.select")}
            options={options?.["service_type"]}
          />
        </Form.Item>

        {billableLines.length > 0 && (
          <>
            <Typography.Title level={5}>
              {t("invoices.linkLines.title")}
            </Typography.Title>
            <Typography.Text type="secondary" className="text-xs">
              {t("invoices.linkLines.hint")}
            </Typography.Text>
            <Flex vertical gap="small" className="mt-2 mb-4">
              {billableLines.map((line) => {
                const checked = line.id in linkedLines
                return (
                  <Flex key={line.id} vertical gap={2}>
                    <Flex align="center" justify="space-between" gap="small">
                      <Checkbox
                        checked={checked}
                        disabled={saving || !!createdInvoice}
                        onChange={(event) =>
                          setLinkedLines((prev) => {
                            const next = { ...prev }
                            if (event.target.checked) {
                              next[line.id] = defaultLinkedLineAmount(
                                line.balance_remaining,
                                amountValue != null ? Number(amountValue) : null,
                                linkedLinesTotal(prev)
                              )
                            } else {
                              delete next[line.id]
                            }
                            return next
                          })
                        }
                      >
                        <Typography.Text>{line.name}</Typography.Text>
                      </Checkbox>
                      <HintedLabel
                        hint={t("invoices.drawer.balanceHint")}
                        testId={`quick-add-hint-balance-${line.id}`}
                        field={t("invoices.drawer.labels.balance")}
                      >
                        <Typography.Text type="secondary" className="text-xs">
                          {t("invoices.drawer.balance", {
                            balance: budgetFormat(line.balance_remaining)
                          })}
                        </Typography.Text>
                      </HintedLabel>
                    </Flex>
                    {checked && (
                      <InputNumber
                        min={0.01}
                        max={Number(line.balance_remaining) || 0}
                        prefix="$"
                        className="w-full"
                        value={linkedLines[line.id]}
                        data-testid={`quick-add-line-amount-${line.id}`}
                        placeholder={t("invoices.linkLines.amountOptional")}
                        onChange={(value) =>
                          setLinkedLines((prev) => ({
                            ...prev,
                            [line.id]: (value as number) ?? 0
                          }))
                        }
                      />
                    )}
                  </Flex>
                )
              })}

              {Object.keys(linkedLines).length > 0 && (
                <Flex align="center" justify="space-between" gap="small">
                  <Typography.Text
                    type={linkedLinesExceed ? "danger" : "secondary"}
                    className="text-xs"
                  >
                    {t("invoices.linkLines.linkedTotal")}
                  </Typography.Text>
                  <Typography.Text
                    type={linkedLinesExceed ? "danger" : undefined}
                    strong
                  >
                    {budgetFormat(String(linkedTotal))}
                  </Typography.Text>
                </Flex>
              )}

              {linkedLinesExceed && (
                <Typography.Text type="danger" className="text-xs">
                  {t("invoices.linkLines.exceedsInvoiceAmount")}
                </Typography.Text>
              )}
            </Flex>
          </>
        )}

        <Typography.Title level={5}>
          {t("finance.quickAddInvoice.sections.attachments")}
        </Typography.Title>

        <Flex vertical gap="small">
          <Upload
            multiple
            listType="text"
            fileList={fileList}
            accept={ATTACHMENT_ACCEPT}
            beforeUpload={stageFile}
            onRemove={removeFile}
            disabled={saving}
            showUploadList={{
              showRemoveIcon: (file) => !saving && file.status !== "done"
            }}
          >
            <Button icon={<UploadOutlined />} disabled={saving}>
              {t("finance.quickAddInvoice.attachments.add")}
            </Button>
          </Upload>
          <Typography.Text type="secondary" className="text-xs">
            {t("finance.quickAddInvoice.attachments.hint", {
              types: ATTACHMENT_ALLOWED_EXTENSIONS.map((ext) =>
                ext.toUpperCase()
              ).join(", "),
              size: ATTACHMENT_MAX_SIZE_MB
            })}
          </Typography.Text>
        </Flex>
      </Form>
    </Drawer>
  )
}
